/**
 * The entry lifecycle's two controls (P7-T1): the autocomplete reuse and the reviewed
 * delete/duplicate, plus the reversible cancellation.
 *
 * Everything here renders: the rules live in `./lifecycle.ts` and the figures arrive already
 * computed from the API's own stored preview. Two properties of this file are deliberate and are
 * what the browser journeys assert:
 *
 *   - the acknowledgement is **not** defaulted. When the stored impact says the commit destroys a
 *     figure that is already effective, the confirm control stays disabled until the user has read
 *     the impact and ticked the acknowledgement, and the commit sends `impactAcknowledged` from
 *     that tick alone. The API's `409` without it is the protection, so nothing here hides it;
 *   - the cancellation is **reversible and named**. The same control cancels an ordinary entry and
 *     restores a canceled one — the two are different routes, and the button says which one it is
 *     about to call rather than being a generic "remove".
 */

import type { EntrySuggestionBody } from '@saldo/contracts';

import type { LifecyclePreviewView } from './lifecycle';
import { formatMoney } from './money';

/* ----------------------------------------------------------- autocomplete reuse --- */

export interface SuggestionPickerProps {
  readonly query: string;
  readonly items: readonly EntrySuggestionBody[];
  /** True while the read is in flight, so the list is never mistaken for "no matches". */
  readonly busy: boolean;
  readonly error: string | null;
  /** What the last pick filled and what it kept, in words (`./lifecycle.ts`). */
  readonly note: string | null;
  readonly disabled: boolean;
  readonly onQueryChange: (query: string) => void;
  readonly onPick: (suggestion: EntrySuggestionBody) => void;
}

export function SuggestionPicker(props: SuggestionPickerProps) {
  const { query, items, busy, error, note, disabled } = props;
  return (
    <div className="field" data-testid="suggestions">
      <label className="field">
        Reuse a past entry
        <input
          data-testid="suggestion-search"
          autoComplete="off"
          placeholder="Type a few letters"
          value={query}
          disabled={disabled}
          onChange={(event) => props.onQueryChange(event.target.value)}
        />
      </label>

      {busy ? <span className="hint">Looking up past entries…</span> : null}
      {error !== null ? (
        <span className="hint bad" data-testid="suggestion-error">
          {error}
        </span>
      ) : null}

      {items.length !== 0 ? (
        <ul className="directory-list" data-testid="suggestion-list">
          {items.map((suggestion) => (
            <li key={`${suggestion.description}|${suggestion.accountId}|${suggestion.signedMinor}`}>
              <button
                type="button"
                data-testid="suggestion-item"
                data-description={suggestion.description}
                disabled={disabled}
                onClick={() => props.onPick(suggestion)}
              >
                {suggestion.description}
                <span className="muted">
                  {' '}
                  · {formatMoney(BigInt(suggestion.signedMinor), suggestion.currency)} ·{' '}
                  {suggestion.occurrences}×
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {note !== null ? (
        <p className="hint" data-testid="suggestion-note">
          {note}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------- the reviewed lifecycle actions --- */

export interface LifecycleReviewProps {
  readonly deleteBlocked: string | null;
  readonly duplicateBlocked: string | null;
  /** The row is canceled: the reversible half is *restoration* rather than a second cancel. */
  readonly canceled: boolean;
  readonly preview: LifecyclePreviewView | null;
  readonly error: string | null;
  readonly busy: boolean;
  readonly acknowledged: boolean;
  readonly onReview: (action: 'delete' | 'duplicate') => void;
  readonly onAcknowledge: (value: boolean) => void;
  readonly onConfirm: () => void;
  readonly onToggleCancellation: () => void;
}

export function LifecycleReview(props: LifecycleReviewProps) {
  const { preview, error, busy, acknowledged, canceled } = props;
  const needsAcknowledgement = preview !== null && preview.impact.requiresConfirmation;
  return (
    <section className="field" aria-label="Entry lifecycle" data-testid="entry-lifecycle">
      <div className="actions">
        <button
          type="button"
          data-testid="entry-cancel-toggle"
          disabled={busy}
          onClick={props.onToggleCancellation}
        >
          {canceled ? 'Restore this entry' : 'Cancel this entry'}
        </button>
        <button
          type="button"
          data-testid="entry-delete-open"
          disabled={busy || props.deleteBlocked !== null}
          title={props.deleteBlocked ?? undefined}
          onClick={() => props.onReview('delete')}
        >
          Review delete
        </button>
        <button
          type="button"
          data-testid="entry-duplicate-open"
          disabled={busy || props.duplicateBlocked !== null}
          title={props.duplicateBlocked ?? undefined}
          onClick={() => props.onReview('duplicate')}
        >
          Review duplicate
        </button>
      </div>

      {props.deleteBlocked !== null ? (
        <p className="hint" data-testid="entry-delete-blocked">
          {props.deleteBlocked}
        </p>
      ) : null}
      {props.duplicateBlocked !== null ? (
        <p className="hint" data-testid="entry-duplicate-blocked">
          {props.duplicateBlocked}
        </p>
      ) : null}
      {error !== null ? (
        <p className="notice bad" data-testid="lifecycle-error">
          {error}
        </p>
      ) : null}

      {preview !== null ? (
        <div data-testid="lifecycle-preview">
          <p data-testid="lifecycle-consequence">{preview.consequence}</p>
          <dl className="status-grid">
            <dt>Amount</dt>
            <dd className="money" data-testid="lifecycle-amount">
              {formatMoney(preview.impact.signedMinor, preview.impact.currency)}
            </dd>
            <dt>Account moves by</dt>
            <dd className="money" data-testid="lifecycle-delta">
              {formatMoney(preview.impact.balanceDeltaMinor, preview.impact.currency)}
            </dd>
            <dt>Effective today</dt>
            <dd data-testid="lifecycle-effective">{preview.impact.effective ? 'yes' : 'no'}</dd>
          </dl>

          {needsAcknowledgement ? (
            <label className="field">
              <input
                type="checkbox"
                data-testid="lifecycle-acknowledge"
                checked={acknowledged}
                disabled={busy}
                onChange={(event) => props.onAcknowledge(event.target.checked)}
              />
              {preview.acknowledgementLabel}
            </label>
          ) : null}

          <div className="actions">
            <button
              type="button"
              className="primary"
              data-testid="lifecycle-confirm"
              disabled={busy || (needsAcknowledgement && !acknowledged)}
              onClick={props.onConfirm}
            >
              {preview.action === 'entry.delete' ? 'Cancel this entry' : 'Create the copy'}
            </button>
            <button type="button" data-testid="lifecycle-reread" disabled={busy} onClick={() => props.onReview(preview.action === 'entry.delete' ? 'delete' : 'duplicate')}>
              Re-read the impact
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
